import { useState } from "react";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface TimePicker24Props {
  label: string;
  value: string; // HH:mm format (e.g., "09:00")
  onChange: (value: string) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  minTime?: string; // HH:mm format (e.g., "09:00")
  maxTime?: string; // HH:mm format (e.g., "09:00")
  minutesStep?: number; // Step for minutes (default: 5)
  error?: boolean;
  helperText?: string;
}

export default function TimePicker24({
  label,
  value,
  onChange,
  disabled = false,
  fullWidth = false,
  size = "medium",
  minTime = "00:00",
  maxTime = "23:55",
  minutesStep = 5,
  error = false,
  helperText,
}: TimePicker24Props) {
  // Convert HH:mm string to Date object
  const timeToDate = (timeString: string): Date | undefined => {
    if (!timeString) return undefined;
    const [hours, minutes] = timeString.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  // Convert Date object to HH:mm string
  const dateToTime = (date: Date | null): string => {
    if (!date) return "";
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const [open, setOpen] = useState(false);

  const handleChange = (newValue: Date | null) => {
    onChange(dateToTime(newValue));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <TimePicker
        label={label}
        value={timeToDate(value)}
        onChange={handleChange}
        disabled={disabled}
        ampm={false}
        minutesStep={minutesStep}
        minTime={timeToDate(minTime)}
        maxTime={timeToDate(maxTime)}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        slotProps={{
          textField: {
            fullWidth,
            size,
            error,
            helperText,
            InputLabelProps: { shrink: true },
            onClick: () => !disabled && setOpen(true),
          },
        }}
      />
    </LocalizationProvider>
  );
}
